import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { ZALO_OA_URL } from '@repo/shared-types';
import { normalizeContactPhone } from '@repo/validation';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { fetchApi } from '../lib/api';
import { useCan } from '../hooks/useCan';
import { useOrgSettings, useUpdateOrg } from '../hooks/useOrgSettings';
import { useAuthStore } from '../stores/authStore';

/** How long "Để sau" hides the strip on this browser. Long enough not to nag,
 *  short enough that an owner who skipped it in week one sees it again while
 *  still on trial. */
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

function isSnoozed(key: string): boolean {
  try {
    const until = Number(localStorage.getItem(key) ?? 0);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
}

function snooze(key: string) {
  try {
    localStorage.setItem(key, String(Date.now() + SNOOZE_MS));
  } catch {
    /* private mode: the strip simply comes back next load */
  }
}

/**
 * One strip under the header, owner/admin only, asking for the one thing the
 * platform has no other way to get: a Zalo number LopHoc can reach the owner
 * on. Registration is email-only and the onboarding wizard's last step is
 * skippable, so for every org that exists today this strip is the only ask.
 *
 * Two messages share the slot, one at a time, in this order:
 *  1. no contact phone → an inline input. The number is saved in place
 *     (PUT /org) and the strip disappears for good once it is stored.
 *  2. phone known but the user's email is unverified → "Gửi lại" the verify
 *     mail. Nothing in the product is gated on verification (deliberately:
 *     a lock here would only cost sign-ups), so this is the one reminder.
 *
 * "Để sau" snoozes the current message 30 days in this browser only, the
 * same localStorage pattern as billing's NudgeBanner. A strip, not a dialog:
 * a teacher opening the app on a phone to mark attendance must never be
 * blocked by a question about contact details (decided 2026-10-04).
 */
export function ContactPhoneBanner() {
  const { t } = useTranslation('settings');
  const canManage = useCan('manage:settings');
  const user = useAuthStore((s) => s.user);
  const organization = useAuthStore((s) => s.organization);
  const { data: org } = useOrgSettings();
  const updateOrg = useUpdateOrg();
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
  // Snooze state is read once per mount into React state so dismissing
  // re-renders; the key carries the org/user id so one browser used for two
  // accounts does not share a snooze.
  const phoneKey = `contact-phone-nudge:${organization?.id ?? ''}`;
  const verifyKey = `verify-email-nudge:${user?.id ?? ''}`;
  const [phoneSnoozed, setPhoneSnoozed] = useState(() => isSnoozed(phoneKey));
  const [verifySnoozed, setVerifySnoozed] = useState(() => isSnoozed(verifyKey));

  if (!canManage || !user || !org) return null;

  const mode: 'phone' | 'verify' | null =
    org.contactPhone === null && !phoneSnoozed ? 'phone' : !user.emailVerified && !verifySnoozed ? 'verify' : null;
  if (!mode) return null;

  const tone = 'border-b border-brand-200 bg-brand-50 text-brand-900';

  if (mode === 'phone') {
    const submit = (e: FormEvent) => {
      e.preventDefault();
      const phone = normalizeContactPhone(draft);
      if (!phone) {
        setInvalid(true);
        return;
      }
      setInvalid(false);
      updateOrg.mutate({ contactPhone: phone });
    };
    return (
      <div className={`px-5 py-2 text-[13px] ${tone}`}>
        <form onSubmit={submit} className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="min-w-0 flex-1 basis-[240px]">
            <span className="mr-2 font-semibold">{t('contactBanner.phoneTitle')}</span>
            <span>{t('contactBanner.phoneBody')} </span>
            <a href={ZALO_OA_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
              {t('contactBanner.zaloLink')}
            </a>
          </span>
          <span className="flex items-center gap-2">
            <Input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (invalid) setInvalid(false);
              }}
              placeholder={t('contactBanner.phonePlaceholder')}
              aria-label={t('contactBanner.phoneTitle')}
              aria-invalid={invalid || undefined}
              className={`h-[36px] w-[160px] bg-white text-[13px] ${invalid ? 'border-red-400' : ''}`}
            />
            <Button type="submit" size="sm" className="h-[36px]" disabled={updateOrg.isPending || draft.trim() === ''}>
              {updateOrg.isPending ? t('profile.saving') : t('contactBanner.save')}
            </Button>
            <button
              type="button"
              onClick={() => {
                snooze(phoneKey);
                setPhoneSnoozed(true);
              }}
              className="flex h-[36px] items-center gap-1 px-1 text-[13px] opacity-70 hover:opacity-100"
              aria-label={t('contactBanner.later')}
            >
              <span className="hidden sm:inline">{t('contactBanner.later')}</span>
              <X className="h-4 w-4" />
            </button>
          </span>
          {invalid && <span className="basis-full text-[12.5px] text-red-600">{t('contactBanner.phoneInvalid')}</span>}
        </form>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between gap-3 px-5 py-2 text-[13px] ${tone}`}>
      <div className="min-w-0 flex-1">
        <span className="mr-2 font-semibold">{t('contactBanner.verifyTitle')}</span>
        <span>{t('contactBanner.verifyBody')}</span>
      </div>
      <button
        type="button"
        disabled={resendState === 'loading' || resendState === 'sent'}
        onClick={async () => {
          setResendState('loading');
          try {
            await fetchApi('/auth/resend-verify', { method: 'POST' });
            setResendState('sent');
          } catch {
            setResendState('error');
          }
        }}
        className="min-h-[36px] shrink-0 font-semibold underline disabled:no-underline disabled:opacity-70"
      >
        {resendState === 'loading'
          ? t('profile.sending')
          : resendState === 'sent'
            ? t('profile.emailSent')
            : resendState === 'error'
              ? t('profile.resendError')
              : t('contactBanner.resend')}
      </button>
      <button
        type="button"
        onClick={() => {
          snooze(verifyKey);
          setVerifySnoozed(true);
        }}
        className="flex h-[36px] shrink-0 items-center opacity-70 hover:opacity-100"
        aria-label={t('contactBanner.later')}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
